import { CheckIcon, UsersIcon } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"
import { EntityIdentity } from "~/components/EntityIdentity"
import { Button } from "~/components/ui/button"
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList
} from "~/components/ui/command"
import {
	Popover,
	PopoverContent,
	PopoverTrigger
} from "~/components/ui/popover"
import { usePermission } from "~/hooks/usePermission"
import { TeamPermissions } from "~/lib/permissions"
import { cn } from "~/lib/utils"
import { api } from "~/trpc/react"

export function IssueWatchers({
	issueId,
	teamId
}: {
	issueId: string
	teamId: string
}) {
	const [open, setOpen] = useState(false)
	const { hasTeamPermission, teamLoaded } = usePermission({
		activeTeamId: teamId
	})
	const canManage =
		teamLoaded && hasTeamPermission(TeamPermissions.MANAGE_TEAM_MEMBERS)
	const state = api.issues.getWatchState.useQuery(
		{ issueId, teamId },
		{ enabled: !!issueId && !!teamId }
	)
	const team = api.team.get.useQuery(
		{ teamId },
		{ enabled: open && canManage && !!teamId }
	)
	const utils = api.useUtils()
	const update = api.issues.setMemberWatchState.useMutation({
		onSuccess: () => utils.issues.getWatchState.invalidate({ issueId, teamId }),
		onError: (error) => toast.error(error.message)
	})
	const watchers = state.data?.watchers ?? []
	const watcherIds = new Set(watchers.map((watcher) => watcher.id))
	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="ghost"
					size="default"
					aria-label={`${watchers.length} watching this issue`}
					disabled={state.isLoading || state.isError}
				>
					{watchers.length ? (
						<span className="flex -space-x-2" aria-hidden="true">
							{watchers.slice(0, 4).map((watcher) => (
								<EntityIdentity
									key={watcher.id}
									kind="user"
									variant="avatar"
									size="md"
									name={watcher?.name ?? "?"}
									image={watcher?.image}
									lastSeenAt={watcher?.lastSeenAt}
									className="ring-2 ring-background"
								/>
							))}
						</span>
					) : (
						<UsersIcon className="size-4" />
					)}
					<span>{watchers.length} watching</span>
				</Button>
			</PopoverTrigger>
			<PopoverContent
				align="end"
				layout="panel"
				aria-label={canManage ? "Manage issue watchers" : "Issue watchers"}
			>
				{canManage ? (
					<Command>
						<CommandInput placeholder="Find a team member…" />
						<CommandList>
							<CommandEmpty>
								{team.isLoading ? "Loading members…" : "No members found"}
							</CommandEmpty>
							<CommandGroup heading="Watch on their behalf">
								{team.data?.teamMembers.map((member) => {
									const watching = watcherIds.has(member.orgMember.userId)
									return (
										<CommandItem
											key={member.id}
											value={`${member.id} ${member.orgMember.user.name}`}
											disabled={update.isPending}
											onSelect={() =>
												update.mutate({
													issueId,
													teamId,
													memberId: member.id,
													watching: !watching
												})
											}
											aria-label={`${watching ? "Unwatch" : "Watch"} on behalf of ${member.orgMember.user.name}`}
										>
											<EntityIdentity
												kind="user"
												userId={member.orgMember.userId}
												name={member.orgMember.user.name}
												image={member.orgMember.user.image}
												size="sm"
											/>
											<CheckIcon
												className={cn(
													"ml-auto size-4",
													!watching && "invisible"
												)}
											/>
										</CommandItem>
									)
								})}
							</CommandGroup>
						</CommandList>
					</Command>
				) : (
					<div className="flex flex-col gap-3">
						{watchers.length ? (
							watchers.map((watcher) => (
								<EntityIdentity
									key={watcher.id}
									kind="user"
									userId={watcher.id}
									name={watcher.name}
									image={watcher.image}
									size="sm"
								/>
							))
						) : (
							<span className="text-sm text-text-small">
								Nobody is watching yet.
							</span>
						)}
					</div>
				)}
			</PopoverContent>
		</Popover>
	)
}
